import { useEffect, useState } from 'react'
import { VETO_THRESHOLD } from '@sh/engine'
import { PolicyTile } from './Board.js'
import { DoveIcon, PowerIcon, PresidentIcon, VetoIcon, VictoryIcon } from './Icons.js'
import { useFinePointer } from '../net/useDevice.js'
import { EVENT_MS } from '../net/useGameEvents.js'
import type { GameEvent, WinReason } from '../net/useGameEvents.js'

/** Why it ended, in the fewest words that still say which rule fired. */
const WIN_REASON: Record<WinReason, string> = {
  liberal_policies: 'Five liberal policies enacted',
  fascist_policies: 'Six fascist policies enacted',
  hitler_executed: 'Hitler was executed',
  hitler_chancellor: 'Hitler was elected Chancellor',
}

/**
 * The backdrop waits this long before it even starts to lift. Scenes queued in
 * one view update play back to back with no gap at all, but two things that
 * happen a moment apart - a policy enacted, then the placard passing once a
 * power has been used - arrive in separate pushes, and without a beat of grace
 * the screen flashed the game between them.
 */
const LINGER_MS = 400
/** And this long to fade, once it has given up waiting. */
const CLOSE_MS = 300

/**
 * The full-bleed moment. Everything the table would watch happen at once on a
 * physical board - a policy going down, somebody being shot, the placard moving
 * - happens on ten phones that are all pointed at different things, so it gets
 * announced on each of them.
 *
 * The backdrop and the scene are deliberately separate. Several of these can
 * queue up behind one another - a rejected government that enacts a chaos
 * policy and then passes the placard - and if each one faded itself out the
 * game screen underneath flashed between them. So the backdrop is raised once,
 * held for as long as anything is waiting to be shown, and lowered only when
 * the queue is empty; the scenes swap underneath it.
 *
 * Every scene dismisses itself; tapping only skips ahead. Nothing here is an
 * input, and nothing here reveals anything that is not already public.
 */
export function Cinematic({
  event,
  onSkip,
  awaitingTap = false,
}: {
  event: GameEvent | null
  onSkip: () => void
  /** This scene will not clear itself, so the hint has to ask rather than offer. */
  awaitingTap?: boolean
}) {
  const fine = useFinePointer()
  // The last scene stays rendered through the wait and the fade, so the
  // backdrop never lifts on an empty screen.
  const [shown, setShown] = useState<GameEvent | null>(event)
  const [stage, setStage] = useState<'hidden' | 'open' | 'closing'>(event ? 'open' : 'hidden')

  useEffect(() => {
    if (event) {
      setShown(event)
      setStage('open')
      return
    }
    if (stage !== 'open') return
    // Anything arriving inside this window cancels the close and simply takes
    // over the backdrop that is already up.
    const timer = window.setTimeout(() => setStage('closing'), LINGER_MS)
    return () => window.clearTimeout(timer)
  }, [event, stage])

  useEffect(() => {
    if (stage !== 'closing') return
    const timer = window.setTimeout(() => {
      setStage('hidden')
      setShown(null)
    }, CLOSE_MS)
    return () => window.clearTimeout(timer)
  }, [stage])

  if (stage === 'hidden' || !shown) return null

  return (
    <button
      type="button"
      className={`cine ${stage === 'closing' ? 'is-closing' : ''}`}
      onClick={() => event && onSkip()}
      data-testid="cinematic"
      data-kind={shown.kind}
    >
      {/*
        Keyed, so each event gets its own mount and replays its own entrance -
        the card flip, the placard drop - without the backdrop moving.
      */}
      <span
        key={shown.id}
        className={`cine__scene cine--${shown.kind} ${
          shown.kind === 'policy' ? `cine--${shown.policy}` : ''
        } ${shown.kind === 'election' ? (shown.passed ? 'cine--elected' : 'cine--rejected') : ''} ${
          shown.kind === 'victory' ? `cine--${shown.winner}` : ''
        } ${
          shown.kind === 'execution' && shown.isYou ? 'cine--fatal' : ''
        }`}
        style={{ ['--cine-ms' as string]: `${EVENT_MS[shown.kind]}ms` }}
      >
        {/*
          The scene holds the screen for several seconds now, and a full-bleed
          panel with no end in sight reads as something that has got stuck. The
          bar is the whole of the reassurance: it says this clears itself, and
          roughly when. A scene that waits for a tap has no countdown to draw -
          it is not going anywhere - and says so in words instead.

          Keyed with the scene, so it restarts on each one without the backdrop
          moving.
        */}
        {!awaitingTap && <span className="cine__progress" aria-hidden="true" />}
        {shown.kind === 'policy' && <PolicyScene event={shown} />}
        {shown.kind === 'execution' && <ExecutionScene event={shown} />}
        {shown.kind === 'veto' && <VetoScene />}
        {shown.kind === 'shuffle' && <ShuffleScene event={shown} />}
        {shown.kind === 'presidency' && <PresidencyScene event={shown} />}
        {shown.kind === 'election' && <ElectionScene event={shown} />}
        {shown.kind === 'victory' && <VictoryScene event={shown} />}
        <span className={`cine__skip ${awaitingTap ? 'cine__skip--required' : ''}`}>
          {awaitingTap
            ? `${fine ? 'click' : 'tap'} anywhere to continue`
            : `${fine ? 'click' : 'tap'} to skip`}
        </span>
      </span>
    </button>
  )
}

function PolicyScene({ event }: { event: Extract<GameEvent, { kind: 'policy' }> }) {
  return (
    <>
      {/* The colour of the room is the answer, so it arrives with the card and
          not before it. */}
      <span className="cine__wash cine__wash--late" aria-hidden="true" />
      <span className="cine__kicker">
        {event.chaos ? 'Three failed elections' : 'The government enacts'}
      </span>
      <span className="cine__flip">
        <span className="cine__face cine__face--back" aria-hidden="true">
          <span className="cine__back-mark">?</span>
        </span>
        <span className="cine__face cine__face--front">
          <PolicyTile policy={event.policy} size="lg" />
        </span>
      </span>
      <span className="cine__title">
        {event.policy === 'liberal' ? 'Liberal policy' : 'Fascist policy'}
      </span>
      <span className="cine__sub">
        Liberal {event.liberal}/5 &middot; Fascist {event.fascist}/6
        {event.chaos && ' — the people acted alone, no power is granted'}
      </span>
    </>
  )
}

function ExecutionScene({ event }: { event: Extract<GameEvent, { kind: 'execution' }> }) {
  return (
    <>
      <span className="cine__wash" aria-hidden="true" />
      <span className="cine__shot" aria-hidden="true" />
      <span className="cine__mark" aria-hidden="true">
        <PowerIcon kind="execution" />
      </span>
      <span className="cine__kicker">{event.isYou ? 'The President chose you' : 'Executed'}</span>
      <span
        className={`cine__title cine__title--name ${event.isYou ? '' : 'cine__title--struck'}`}
      >
        {event.isYou ? 'You have been executed' : event.name}
      </span>
      <span className="cine__sub">
        {event.isYou
          ? 'No speaking, no voting, no office. Keep what you know to yourself.'
          : `${event.name} is out of the game entirely — no speaking, no voting, no office.`}
      </span>
    </>
  )
}

/**
 * The Veto Power arming itself, which is the only scene here that nobody did.
 *
 * It earns the full screen for the same reason the band at the foot of the
 * fascist track exists at all: the rule is not printed under a slot on the
 * physical board, it arrives in the same breath as a fascist policy and
 * usually an execution, and a table that has spent five rounds watching the
 * tracks will simply not notice a strip of red text changing wording. By the
 * time it matters the Chancellor has two tiles in hand and about twenty
 * seconds, which is not the moment to be learning the rule.
 *
 * It says what the government can now do rather than only that something
 * unlocked, because the half of the rule that decides games is the price: a
 * killed agenda is a failed election, and three of those are chaos.
 */
function VetoScene() {
  return (
    <>
      <span className="cine__wash" aria-hidden="true" />
      <span className="cine__stamp" aria-hidden="true">
        <span className="cine__stamp-ring" />
        <VetoIcon />
      </span>
      <span className="cine__kicker">The {VETO_THRESHOLD}th fascist policy</span>
      <span className="cine__title">Veto power in effect</span>
      <span className="cine__sub">
        From now on the Chancellor may move to kill the agenda outright. If the President agrees,
        both tiles are discarded, nothing is enacted — and it counts as a failed election.
      </span>
    </>
  )
}

/**
 * The two piles becoming one again.
 *
 * The board already draws both piles and already prints both counts, and this
 * still earns a scene, because the counts swapping is the quietest way the
 * game has of destroying information. Everything a careful player knows about
 * what is left to draw is built out of watching tiles leave the deck, and a
 * reshuffle voids all of it at a stroke - at a physical table that is somebody
 * audibly gathering fifteen cards and shuffling them, which nobody misses.
 *
 * So it is shown as the gather: the pile comes together from off the sides of
 * the screen and settles into one stack with the new count on its face. The
 * faces stay blank, as both piles are everywhere else in this app - what is in
 * either of them is never revealed to anybody, and a scene about the deck is
 * the last place to start.
 */
function ShuffleScene({ event }: { event: Extract<GameEvent, { kind: 'shuffle' }> }) {
  return (
    <>
      <span className="cine__wash" aria-hidden="true" />
      {/* Labelled like the piles on the board are: the cards themselves are
          decoration, but the number on their face is the one figure the scene
          has and it is not hidden along with them. */}
      <span className="cine__gather" aria-label={`Draw pile: ${event.deck} policies`}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <span
            key={i}
            className="cine__gather-card"
            style={{ ['--i' as string]: i }}
            aria-hidden="true"
          />
        ))}
        <span className="cine__gather-count">{event.deck}</span>
      </span>
      <span className="cine__kicker">Fewer than three tiles left</span>
      <span className="cine__title">The deck is reshuffled</span>
      <span className="cine__sub">
        The discards go back in. Any count the table was keeping is void.
      </span>
    </>
  )
}

function PresidencyScene({ event }: { event: Extract<GameEvent, { kind: 'presidency' }> }) {
  return (
    <span className="cine__placard">
      <span className="cine__placard-icon" aria-hidden="true">
        <PresidentIcon />
      </span>
      <span className="cine__kicker">
        {event.special ? 'Special election' : 'The presidency passes'}
      </span>
      <span className="cine__title cine__title--name">
        {event.isYou ? 'You are President' : event.name}
      </span>
    </span>
  )
}

/**
 * The end of the evening. Everything else on a phone is an interruption to be
 * got through; this one is the point, so it takes the whole screen in the
 * winning colour and holds it.
 */
function VictoryScene({ event }: { event: Extract<GameEvent, { kind: 'victory' }> }) {
  return (
    <>
      <span className="cine__wash" aria-hidden="true" />
      <span className="cine__burst" aria-hidden="true" />
      <span className="cine__burst cine__burst--late" aria-hidden="true" />
      {/*
        The side's own emblem rather than a policy card. A liberal win is as
        often Hitler being shot as it is five liberal policies, and a blue tile
        standing in for it said the wrong thing; a dove and an eagle each say
        theirs at a glance, and the eagle is already the fascists' mark on the
        board's last slot and in the tab.
      */}
      <span className="cine__trophy cine__trophy--emblem">
        {event.winner === 'liberal' ? <DoveIcon /> : <VictoryIcon />}
      </span>
      <span className="cine__title cine__title--win">
        {event.winner === 'liberal' ? 'Liberals win' : 'Fascists win'}
      </span>
      <span className="cine__kicker cine__kicker--late">{WIN_REASON[event.reason]}</span>
      {event.youWon !== null && (
        <span className={`cine__verdict ${event.youWon ? 'is-won' : 'is-lost'}`}>
          {event.youWon ? 'You win' : 'You lose'}
        </span>
      )}
    </>
  )
}

/**
 * One side of the ballot: how many, and then who.
 *
 * The names are under the count rather than beside it because the count is
 * read from across the table and the names are read by the person holding the
 * phone, and because two columns of names under two numbers is the shape the
 * question actually has - this lot against that lot.
 */
function Side({
  tone,
  label,
  voters,
}: {
  tone: 'ja' | 'nein'
  label: string
  voters: string[]
}) {
  return (
    <span className={`cine__tally-half cine__tally-half--${tone}`}>
      <b>{voters.length}</b>
      <span className="cine__tally-label">{label}</span>
      {/*
        A side nobody took is left blank under its rule. The count above it
        already says nought, and a word there only invited the table to read
        it as a name.
      */}
      <span className="cine__voters">
        {voters.map((who, i) => (
          <span key={`${who}-${i}`} className="cine__voter">
            {who}
          </span>
        ))}
      </span>
    </span>
  )
}

/**
 * How the ballot went, and who put it there.
 *
 * Both outcomes get a scene. A rejection always had one; an election that
 * passed had none at all, and simply became a legislative session on the
 * President's phone while the rest of the table was still working out whether
 * it had carried. It is the same screen either way on purpose - the thing the
 * table wants to see is the same thing, and the only honest difference between
 * the two is the word at the top and what happens to the tracker.
 *
 * Nothing here is a disclosure. A resolved ballot is public by the rules and
 * by the wire - the board has printed both lists for as long as it has existed
 * - and the one ballot this app does keep to itself, your own before the vote
 * resolves, is never in `lastVote` to begin with.
 */
function ElectionScene({ event }: { event: Extract<GameEvent, { kind: 'election' }> }) {
  return (
    <>
      <span className="cine__wash" aria-hidden="true" />
      <span className="cine__kicker">
        {event.passed ? 'The government is elected' : 'The government is rejected'}
      </span>
      {/* Who was on the ballot, because by the time this lands the screen has
          already moved on and a table screen never had it in the first place. */}
      <span className="cine__gov">
        {event.president} &amp; {event.chancellor}
      </span>
      <span className="cine__tally">
        <Side tone="ja" label="Ja" voters={event.ja} />
        <Side tone="nein" label="Nein" voters={event.nein} />
      </span>
      {!event.passed && (
        <span className="cine__dots" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={`cine__dot ${i < event.tracker ? 'is-on' : ''} ${
                i === event.tracker - 1 ? 'is-new' : ''
              }`}
            />
          ))}
        </span>
      )}
      <span className="cine__sub">
        {event.passed
          ? `${event.chancellor} takes the Chancellery. The legislative session begins.`
          : event.tracker >= 3
            ? 'The country falls into chaos.'
            : `Election tracker ${event.tracker} of 3 — the presidency moves on.`}
      </span>
    </>
  )
}
